<template>
  <li
    class="review row pb-3 md:pb-4 lg:pb-5"
    :class="comment.is_answer ? 'ml-6' : 'md:mx-0'"
  >
    <!-- photo -->
    <div class="col-2 pr-0 pl-0">
      <div
        v-if="comment.imageUrl"
        class="w-100 mb-0 bg-white pattern-2"
      >
        <RemoteImage
          :src="comment.is_answer ? defaultReviewImg : comment.imageUrl"
          img-class="w-100 mb-n1"
          :alt="comment.shortName"
          sizes="143px"
        />
      </div>
      <div
        v-else
        class="relative"
      >
        <div class="embed-responsive embed-responsive-1by1 mb-0 bg-white pattern-2" />
        <div class="mb-0 absolute bottom-0 left-0 w-120">
          <BirdSkin class="w-100 mb-n1" />
        </div>
      </div>
    </div>
    <!-- review -->
    <div class="col-10 pl-4 md:pl-5 md:mr-0 flex flex-col">
      <div class="shadow block py-3 px-4 rounded-lg comment-bubble relative">
        <div
          class="row items-center order-1 lg:order-0 text-sm"
          :class="registeredUser ? 'mb-1' : 'mb-0 md:mb-3'"
        >
          <!-- name + date -->
          <div class="flex w-100 justify-start flex-wrap">
            <div
              v-if="comment.verifiedComment"
              v-tooltip="{
                content: t('verified_user_message'),
                theme: 'verifiedUserTooltip',
              }"
              class="absolute zindex-11 mt-n5 ml-n4"
            >
              <VerifiedUserSvg />
            </div>
            <strong class="col-auto order-0">
              {{ comment.is_answer ? `Mixit team -> ${ comment.shortName }` : comment.shortName }}
            </strong>
            <ProductStarsRating
              v-if="!comment.is_answer && comment.starCount"
              :rating="comment.starCount"
              origin="cs"
              :show-rating="false"
              :show-origin="false"
              :mobile-minified="false"
              add-class="xl:order-1 order-2 xl:ml-0 ml-2 lg:mt-0 mt-1 xl:flex-full"
            />
            <span class="col-auto flex-shrink-0 ml-auto xl:order-2 order-1">{{ comment.createdAt }}</span>
          </div>
        </div>
        <div
          v-if="registeredUser && !comment.verifiedComment"
          class="flex mb-2 items-center"
        >
          <span>{{ t('registered_user') }}</span>
          <span
            v-tooltip="{
              content: t('registered_user_message'),
              theme: 'baseTooltip',
            }"
            class="bg-warning p-1 rounded-circle ml-1"
          >
            <i class="mi-info icon-14" />
          </span>
        </div>
        <!-- review text -->
        <p class="mb-1 lg:mb-0">
          {{ comment.body }}
        </p>
      </div>
    </div>
  </li>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

import defaultReviewImg from '@/assets/images/default-review-image.png'
import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import ProductStarsRating from '@/components/products/ProductStarsRating.vue'
import VerifiedUserSvg from '@/components/svg/VerifiedUserSvg.vue'
import BirdSkin from '@/components/svg/birds/BirdSkin.vue'

const { t } = useI18n()

const registeredUser = true

defineProps({
  comment: {
    type: Object,
    default() {
      return {}
    },
  },
})
const skins = [
    'cowboy',
    'wizard',
    'warrior',
    'postman',
    'cook',
    'fitness'
];
</script>

<style lang="scss" scoped>
.xl\:flex-full {
  flex: 0 0 100%;
  @media (min-width: 1200px) {
    flex: auto;
  }
}
.comment-bubble {
  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #f7e7d1;
    right: 100%;
    top: 30px;

    @media screen and (max-width: 991px) {
      border-right: 10px solid #ffffff;
    }
    @media screen and (max-width: 767px) {
      top: 20px;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentář napsal zaregistrovaný zákazník",
    "verified_user_message": "Ověřený nákup - zákazník produkt zakoupil na e-shopu Mixit"
},
    "sk": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentár napísal registrovaný zákazník",
    "verified_user_message": "Overený nákup - zákazník produkt zakúpil na e-shope Mixit"
},
    "pl": {
    "registered_user": "Zarejestrowany klient",
    "registered_user_message": "Komentarz zarejestrowanego klienta.",
    "verified_user_message": "Zweryfikowany zakup - klient zakupił produkt w sklepie internetowym Mixit"
},
    "hu": {
    "registered_user": "Regisztrált ügyfél",
    "registered_user_message": "Ezt a értékelést Mixit regisztrált ügyfél írta.",
    "verified_user_message": "Ellenőrzött vásárlás - vásárló a Mixit webshopban vásárolta meg ezt a terméket"
},
    "de": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem registrierten Kunden verfasst.",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat das Produkt bereits gekauft."
},
    "at": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem registrierten Kunden verfasst.",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat das Produkt bereits gekauft."
},
    "ro": {
    "registered_user": "Client înregistrat",
    "registered_user_message": "Comentariu scris de un client înregistrat\n\n\n\n\n",
    "verified_user_message": "Achiziție verificată - clientul a cumpărat produsul de pe e-shopul Mixit.\n\n\n\n\n\n\n"
},
    "fr": {
    "registered_user": "Client enregistré",
    "registered_user_message": "Commentaire écrit par un client enregistré",
    "verified_user_message": "Achat vérifié : le client a acheté le produit sur la e-shop Mixit"
}
  }
</i18n>
